<script setup>
import { ref, computed, watch } from 'vue';
import router from '@/router/routes';
import userAvatar from '@/components/userAvatar.vue';
import { useBusinessPartnersStore } from '@/stores/businessPartners';
import { useUserStore } from '@/stores/user';
import DecisionMaker from '@/components/businessPartner/DecisionMaker.vue';



const businessPartnersStore = useBusinessPartnersStore();
const userStore = useUserStore();
const managerHost = import.meta.env.VITE_MANAGER_HOST;


// Optional props to allow fallback rendering of DecisionMaker if slot is not provided
const dmProps = defineProps({
  currentContact: { type: Object, required: false, default: null },
  contacts: { type: Array, required: false, default: undefined },
  currentContactIndex: { type: Number, required: false, default: 0 },
  editMode: { type: Boolean, required: false, default: false },
  onPrev: { type: Function, required: false, default: undefined },
  onNext: { type: Function, required: false, default: undefined },
  onAdd: { type: Function, required: false, default: undefined },
  onEdit: { type: Function, required: false, default: undefined },
  onSave: { type: Function, required: false, default: undefined },
  onUndo: { type: Function, required: false, default: undefined },
  copyInputValue: { type: Function, required: false, default: undefined }
});

// Status color helpers
function statusColorClass(color) {
  const c = (color || '').toLowerCase();
  if (c === 'green') return 'color-green';
  if (c === 'red') return 'color-red';
  if (c === 'orange' || c === 'yellow') return 'color-orange';
  return 'color-neutral';
}
const creditWorthinessEntries = computed(() => businessPartnersStore.businessPartner?.creditWorthiness || []);
const communicationBanEntries = computed(() => businessPartnersStore.businessPartner?.communicationBans || []);
const creditExpanded = ref(true);
const bansExpanded = ref(true);
const isSingleCreditStatus = computed(() => creditWorthinessEntries.value.length === 1);
const isSingleBanStatus = computed(() => communicationBanEntries.value.length === 1);
function compactStatusText(entry) {
  const raw = (entry?.description || '').trim();
  if (!raw) return 'n/a';
  const symbolMatch = raw.match(/\[[^\]]+\]/);
  const symbol = symbolMatch ? `${symbolMatch[0]} ` : '';
  const parts = raw.split(':');
  let text = (parts.length > 1 ? parts.slice(1).join(':') : raw).trim();
  if (symbolMatch) {
    text = text.replace(symbolMatch[0], '').trim();
  }
  if (!text) return 'n/a';
  const normalized = text.charAt(0).toUpperCase() + text.slice(1);
  return `${symbol}${normalized}`.trim();
}
function statusTitle(label, entry) {
  return `${label}: ${compactStatusText(entry)}`;
}
// Dominant color of a list of entries (for the collapsed summary dot)
function dominantColor(entries) {
  if (!entries || !entries.length) return 'neutral';
  if (entries.some(e => (e.color||'').toLowerCase() === 'red')) return 'red';
  if (entries.some(e => ['orange','yellow'].includes((e.color||'').toLowerCase()))) return 'orange';
  if (entries.every(e => (e.color||'').toLowerCase() === 'green')) return 'green';
  return 'neutral';
}

// Lists (Segmente)
const listsExpanded = ref(false);
function toggleListsExpanded(){ listsExpanded.value = !listsExpanded.value; }
const showNames = ref(false);
const uniqueListUsers = computed(()=>{
  const seen = new Set();
  const order = [];
  for(const list of (businessPartnersStore.businessPartner?.lists||[])){
    const u = (list.colleaguesWithAccess||[])[0];
    if(u && !seen.has(u.id)) { seen.add(u.id); order.push(u); }
  }
  return order.slice(0,12);
});
const userListCounts = computed(()=>{
  const counts = {};
  for(const list of (businessPartnersStore.businessPartner?.lists||[])){
    const u = (list.colleaguesWithAccess||[])[0];
    if(u && u.id){ counts[u.id] = (counts[u.id]||0)+1; }
  }
  return counts;
});
const listSearch = ref('');
const filteredLists = computed(()=>{
  const all = businessPartnersStore.businessPartner?.lists || [];
  const q = listSearch.value.trim().toLowerCase();
  if(!q) return all;
  return all.filter(l => (l.name||'').toLowerCase().includes(q));
});
const firstList = computed(() => (businessPartnersStore.businessPartner?.lists || [])[0]);
function prefersTopRight(list){
  if(!list) return false;
  const name = (list.name||'').trim();
  if(name.length > 18) return true;
  if(name.length > 14 && name.includes(' ')) return true;
  return false;
}
const list0Classes = computed(() => ({
  'is-invalid': firstList.value?.validPeriod === false,
  'has-status-top': firstList.value ? prefersTopRight(firstList.value) : false
}));
function listInlineClasses(list){
  if(!list) return {};
  return {
    'is-invalid': list.validPeriod === false,
    'has-status-top': prefersTopRight(list)
  };
}
function openList(list){
  if(!list || list.validPeriod === false) return;
  userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerList = list.id;
  router.push({ name: 'BusinessPartnerList', query: { source: 'filter', filter: userStore.currentFilter.filter.signature } });
}

// Sales Matrix helpers (reuse reportingHelpers from parent logic minimally)
function toNumber(v){ const n = Number(v); return Number.isFinite(n) ? n : 0; }
const revenueDirKeys = ['oe','tb','gs','omp'];
const rawSalesValue = computed(()=> businessPartnersStore.businessPartner?.salesValues || { oe:null, tb:null, gs:null, omp:null, total:null });
const maxRevenue = computed(()=> Math.max(...revenueDirKeys.map(k => toNumber(rawSalesValue.value?.[k]))));
function formatRevenue(value){ const n = toNumber(value); if(n === 0) return '0'; try { return n.toLocaleString('de-DE'); } catch { return String(n); } }
function revenueAmount(key){ return toNumber(rawSalesValue.value?.[key]); }
function revenueTooltip(key){ const amount = revenueAmount(key); if(!amount) return 'Kein Umsatz'; return `Umsatz: ${formatRevenue(amount)} €`; }
const computedRevenueTotal = computed(()=> {
  const parts = revenueDirKeys.map(k => toNumber(rawSalesValue.value?.[k]));
  const sum = parts.reduce((a,b)=> a+b, 0);
  const backend = toNumber(rawSalesValue.value?.total);
  if(backend > 0 && sum === 0) return backend;
  if(backend > 0 && sum > 0 && Math.abs(backend - sum)/sum < 0.01) return backend;
  return sum;
});

// Controls visibility of the Sales Matrix (reports) accordion content
const showDirectories = ref(true);

function formatWebsiteLink(link) {
  if (!link) return '#';
  if (!link.startsWith('http://') && !link.startsWith('https://')) {
    return `https://${link}`;
  }
  return link;
}
function copyInputValue(event) {
  const button = event.target.closest('button');
  const input = button?.parentElement.querySelector('input');
  if (input && input.value) {
    navigator.clipboard.writeText(input.value).catch(()=>{});
  }
}
function handleLinkClick(event, text) {
  const explicit = (typeof text === 'string' && text.trim() !== '');
  let value = explicit ? text.trim() : '';
  if(!explicit) return; // only copy explicit here to avoid surprises
  navigator.clipboard.writeText(value).catch(()=>{});
}
</script>

<template>
  <div class="customer-details">
    <div class="accordion-content-wrapper">
      <div class="customer-details-content" v-if="businessPartnersStore.businessPartner">
        <div class="headline-wrapper">
          <div class="txt-wrapper">
            <div class="businessPartnerNameWrapper">
              <div class="bp-header-row">
                <span class="bp-customer-name">{{ businessPartnersStore.businessPartner.gp.alias }}</span>
                <div class="bp-header-actions">
                  <span class="bp-pill gp" :title="'GP Nummer'">GP: <strong>{{ businessPartnersStore.businessPartner.gp.no }}</strong></span>
                  <a
                    v-if="businessPartnersStore.businessPartner?.addressList?.[0]?.name"
                    class="bp-action-chip"
                    target="_blank"
                    :title="'Google-Suche: ' + businessPartnersStore.businessPartner.gp.alias"
                    :href="'https://www.google.com/search?q=' + encodeURIComponent((businessPartnersStore.businessPartner?.gp?.name || '') + ' ' + (businessPartnersStore.businessPartner?.addressList?.[0]?.name || ''))"
                  >
                    <img src="@/assets/imgs/logos/google/Google__G__logo.png" alt="Google" />
                  </a>
                  <a
                    class="bp-action-chip manager"
                    :href="managerHost + '/?sugId=' + businessPartnersStore.businessPartner.idSugar"
                    target="_blank"
                    title="Im SELLWERK Manager öffnen"
                  >
                    <img src="@/assets/imgs/logos/sellwerk/sellwerk_manager_logo_small.svg" alt="SELLWERK Manager" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="container accordion-menu bp-statuses" v-if="businessPartnersStore.businessPartner.gp">
          <template v-if="isSingleCreditStatus">
            <div class="bp-status-single-row" :class="statusColorClass(creditWorthinessEntries[0]?.color)" :title="statusTitle('Mahnstufe', creditWorthinessEntries[0])">
              <span class="bp-status-dot"></span>
              <span class="bp-status-title">Mahnstufe:</span>
              <span class="bp-status-text">{{ compactStatusText(creditWorthinessEntries[0]) }}</span>
            </div>
          </template>
          <template v-else>
            <button
              type="button"
              class="collapse-toggle reports-toggle list-toggle dsm-toggle"
              @click="creditExpanded = !creditExpanded"
              :aria-expanded="creditExpanded ? 'true' : 'false'"
              :title="creditExpanded ? 'Mahnstufe einklappen' : 'Mahnstufe ausklappen'"
            >
              <h4 class="achieved left salesMatrix">Mahnstufe</h4>
              <img class="bp-status-chevron" :class="{ rotated: creditExpanded }" src="@/assets/imgs/icons/chevron-up-solid.svg" alt="" aria-hidden="true" />
              <span class="state toggle-icon" aria-hidden="true">
                <span class="bar bar-h"></span>
                <span class="bar bar-v" :class="{collapsed: creditExpanded}"></span>
              </span>
            </button>
            <transition name="fade-slide">
              <div v-if="creditExpanded" class="bp-status-section single">
                <ul class="bp-accordion-list" role="list">
                  <li
                    v-for="(entry, i) in creditWorthinessEntries"
                    :key="i"
                    class="bp-status-entry"
                    :class="statusColorClass(entry.color)"
                    :title="entry.description"
                  >
                    <span class="bp-status-dot"></span>
                    <span class="bp-status-text">{{ entry.description }}</span>
                  </li>
                  <li v-if="!creditWorthinessEntries.length" class="bp-status-entry color-neutral">
                    <span class="bp-status-dot"></span>
                    <span class="bp-status-text">n/a</span>
                  </li>
                </ul>
              </div>
            </transition>
          </template>
        </div>
        <div class="container accordion-menu bp-statuses" v-if="businessPartnersStore.businessPartner.gp">
          <template v-if="isSingleBanStatus">
            <div class="bp-status-single-row" :class="statusColorClass(communicationBanEntries[0]?.color)" :title="statusTitle('Sperren', communicationBanEntries[0])">
              <span class="bp-status-dot"></span>
              <span class="bp-status-title sperren-title">Sperren:</span>
              <span class="bp-status-text">{{ compactStatusText(communicationBanEntries[0]) }}</span>
            </div>
          </template>
          <template v-else>
            <button
              type="button"
              class="collapse-toggle reports-toggle list-toggle dsm-toggle"
              @click="bansExpanded = !bansExpanded"
              :aria-expanded="bansExpanded ? 'true' : 'false'"
              :title="bansExpanded ? 'Sperren einklappen' : 'Sperren ausklappen'"
            >
              <h4 class="achieved left salesMatrix sperren-heading">Sperren</h4>
              <img class="bp-status-chevron" :class="{ rotated: bansExpanded }" src="@/assets/imgs/icons/chevron-up-solid.svg" alt="" aria-hidden="true" />
              <span class="state toggle-icon" aria-hidden="true">
                <span class="bar bar-h"></span>
                <span class="bar bar-v" :class="{collapsed: bansExpanded}"></span>
              </span>
            </button>
            <transition name="fade-slide">
              <div v-if="bansExpanded" class="bp-status-section single">
                <ul class="bp-accordion-list" role="list">
                  <li
                    v-for="(entry, i) in communicationBanEntries"
                    :key="i"
                    class="bp-status-entry"
                    :class="statusColorClass(entry.color)"
                    :title="entry.description"
                  >
                    <span class="bp-status-dot"></span>
                    <span class="bp-status-text">{{ entry.description }}</span>
                  </li>
                  <li v-if="!communicationBanEntries.length" class="bp-status-entry color-neutral">
                    <span class="bp-status-dot"></span>
                    <span class="bp-status-text">n/a</span>
                  </li>
                </ul>
              </div>
            </transition>
          </template>
        </div>
        <div class="container">
          <div style="display: flex; align-items: center; justify-content: space-between;">
            <h4 class="achieved left lists" style="margin-bottom:0;">Segmente</h4>
          </div>
          <div class="listsWrapper">
            <template v-if="(businessPartnersStore.businessPartner.lists||[]).length === 1">
              <div class="lists-expanded-area lists-inline" role="list" aria-label="Listen">
                <div class="legend-names-toggle-row">
                  <div class="status-legend">
                          <span class="legend-dot legend-dot-aktiv"></span>
                    <span class="legend-label">aktiv</span>
                    <span class="legend-dot legend-dot-inaktiv"></span>
                    <span class="legend-label">inaktiv</span>
                  </div>
                  <button type="button" class="show-names-btn modern-toggle" @click="showNames = !showNames">
                    <span>{{ showNames ? 'Namen ausblenden' : 'Namen einblenden' }}</span>
                  </button>
                </div>
                      <span class="singleList mini" :class="listInlineClasses(businessPartnersStore.businessPartner.lists[0])" @click="businessPartnersStore.businessPartner.lists[0].validPeriod!==false && openList(businessPartnersStore.businessPartner.lists[0])" :tabindex="businessPartnersStore.businessPartner.lists[0].validPeriod!==false ? 0 : -1" @keydown.enter.prevent="businessPartnersStore.businessPartner.lists[0].validPeriod!==false && openList(businessPartnersStore.businessPartner.lists[0])" @keydown.space.prevent="businessPartnersStore.businessPartner.lists[0].validPeriod!==false && openList(businessPartnersStore.businessPartner.lists[0])" role="listitem" :aria-label="businessPartnersStore.businessPartner.lists[0].name + ' ' + (businessPartnersStore.businessPartner.lists[0].validPeriod===false ? 'inaktiv' : 'aktiv')" style="display: block;">
                  <div class="list-header-row" style="display: flex; align-items: center; width: 100%;">
                          <span class="avatars-pill avatars-row" :data-count="(businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess||[]).length">
                            <span v-for="user in (businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess||[])" :key="user.id" class="avatar-wrapper">
                              <userAvatar :idUser="user.id" :size="18" :layout="'row'" :showSalesAgent="true" :hideText="!showNames" :hideRank="showNames" />
                            </span>
                            <div v-if="!(businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess && businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess.length)" class="avatar-placeholder mini" aria-hidden="true">–</div>
                          </span>
                    <span class="name" :class="{'invalid': businessPartnersStore.businessPartner.lists[0].validPeriod===false}" style="margin-left: 1.1em;">{{ businessPartnersStore.businessPartner.lists[0].name }}</span>
                  </div>
                  <div v-if="businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess && businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess.length" class="colleagues-descriptions-row" style="display: flex; flex-direction: column; margin:0.2em 0 0.2em 0.5em; gap: 0.7em;">
                    <span v-for="user in businessPartnersStore.businessPartner.lists[0].colleaguesWithAccess.filter(u => u && u.sugar && u.sugar.description)" :key="(user && user.id ? user.id : 'unknown') + '-desc'" class="colleague-description" style="background: #f7f7fa; border-radius: 0.6em; padding: 0.7em 1.2em; font-size:0.98em; color:#444; display: flex; align-items: center; box-shadow: 0 1px 2px #0001; margin-bottom: 0.3em;">
                      <span class="desc-icon" style="margin-right:0.7em; color:#888;"><!-- custom icon, z.B. Notiz-Icon --><svg width="16" height="16" viewBox="0 0 20 20" fill="none"><rect x="3" y="4" width="14" height="12" rx="2" stroke="#888" stroke-width="1.2"/><path d="M6 7h8M6 10h8M6 13h5" stroke="#888" stroke-width="1.1" stroke-linecap="round"/></svg></span>
                      <span class="desc-text">{{ user.sugar.description }}</span>
                    </span>
                  </div>
                </span>
              </div>
            </template>
            <div class="lists-compact-header" v-else>
              <button type="button" class="avatar-expander" :aria-expanded="listsExpanded ? 'true':'false'" @click="toggleListsExpanded">
                <span class="avatars-row" :data-count="uniqueListUsers.length">
                  <span v-for="user in uniqueListUsers" :key="user.id" class="avatar-wrapper">
                    <userAvatar :idUser="user.id" :size="24" :layout="'row'" :showSalesAgent="true" :hideText="true" />
                    <span v-if="userListCounts[user.id] > 1" class="avatar-count-badge" :title="userListCounts[user.id] + ' Listen'">{{ userListCounts[user.id] }}</span>
                  </span>
                </span>
                <span class="chevron" aria-hidden="true">
                  <img :class="{rotated:listsExpanded}" src="@/assets/imgs/icons/chevron-up-solid.svg" alt="toggle" />
                </span>
              </button>
              <div class="lists-tools">
                <input type="text" v-model="listSearch" placeholder="Suchen..." class="list-search" />
              </div>
            </div>
            <transition name="fade-slide" v-if="(businessPartnersStore.businessPartner.lists||[]).length > 1">
              <div v-if="listsExpanded" class="lists-expanded-area lists-inline" role="list" aria-label="Listen">
                <div class="legend-names-toggle-row">
                  <div class="status-legend">
                    <span class="legend-dot legend-dot-aktiv"></span>
                    <span class="legend-label">aktiv</span>
                    <span class="legend-dot legend-dot-inaktiv"></span>
                    <span class="legend-label">inaktiv</span>
                  </div>
                  <button type="button" class="show-names-btn modern-toggle" @click="showNames = !showNames">
                    <span>{{ showNames ? 'Namen ausblenden' : 'Namen einblenden' }}</span>
                  </button>
                </div>
                <p v-if="filteredLists.length === 0" class="lists-empty">Keine Treffer.</p>
                <span v-for="list in filteredLists" :key="list.id" class="singleList mini" :class="listInlineClasses(list)" @click="list.validPeriod!==false && openList(list)" :tabindex="list.validPeriod!==false ? 0 : -1" @keydown.enter.prevent="list.validPeriod!==false && openList(list)" @keydown.space.prevent="list.validPeriod!==false && openList(list)" role="listitem" :aria-label="list.name + ' ' + (list.validPeriod===false ? 'inaktiv' : 'aktiv')" style="display: block;">
                  <div class="list-header-row" style="display: flex; align-items: center; width: 100%;">
                    <span class="avatars-pill avatars-row" :data-count="(list.colleaguesWithAccess||[]).length">
                      <span v-for="user in (list.colleaguesWithAccess||[])" :key="user.id" class="avatar-wrapper">
                        <userAvatar :idUser="user.id" :size="18" :layout="'row'" :showSalesAgent="true" :hideText="!showNames" :hideRank="showNames" />
                      </span>
                      <div v-if="!(list.colleaguesWithAccess && list.colleaguesWithAccess.length)" class="avatar-placeholder mini" aria-hidden="true">–</div>
                    </span>
                    <span class="name" :class="{'invalid': list.validPeriod===false}" style="margin-left: 1.1em;">{{ list.name }}</span>
                  </div>
                  <div v-if="list.colleaguesWithAccess && list.colleaguesWithAccess.length" class="colleagues-descriptions-row" style="display: flex; flex-direction: column; margin:0.2em 0 0.2em 0.5em; gap: 0.7em;">
                    <span v-for="user in list.colleaguesWithAccess.filter(u => u && u.sugar && u.sugar.description)" :key="(user && user.id ? user.id : 'unknown') + '-desc'" class="colleague-description" style="background: #f7f7fa; border-radius: 0.6em; padding: 0.7em 1.2em; font-size:0.98em; color:#444; display: flex; align-items: center; box-shadow: 0 1px 2px #0001; margin-bottom: 0.3em;">
                      <span class="desc-icon" style="margin-right:0.7em; color:#888;"><!-- custom icon, z.B. Notiz-Icon --><svg width="16" height="16" viewBox="0 0 20 20" fill="none"><rect x="3" y="4" width="14" height="12" rx="2" stroke="#888" stroke-width="1.2"/><path d="M6 7h8M6 10h8M6 13h5" stroke="#888" stroke-width="1.1" stroke-linecap="round"/></svg></span>
                      <span class="desc-text">{{ user.sugar.description }}</span>
                    </span>
                  </div>
                </span>
              </div>
            </transition>
          </div>
        </div>
        <div :class="['container accordion-menu reports', {'deactivated' : !businessPartnersStore.businessPartner.kstLinks || businessPartnersStore.businessPartner.kstLinks.length === 0}]" style="margin-top:.9rem;">
          <button type="button" class="collapse-toggle reports-toggle list-toggle dsm-toggle" @click="showDirectories = !showDirectories" :aria-expanded="showDirectories ? 'true':'false'" :title="showDirectories ? 'Sales Matrix einklappen' : 'Sales Matrix ausklappen'">
            <h4 class="achieved left salesMatrix"> Sales Matrix</h4>
            <span class="state toggle-icon" aria-hidden="true">
              <span class="bar bar-h"></span>
              <span class="bar bar-v" :class="{collapsed: showDirectories}"></span>
            </span>
          </button>
          <transition name="fade-slide">
            <div v-if="showDirectories" class="dsm-wrapper" role="region" aria-labelledby="dsm-table-caption">
              <table class="report-table" aria-describedby="dsm-table-caption">
                <caption id="dsm-table-caption" class="sr-only">Sales Matrix: Präsenz, Umsatz & Verträge in Verzeichnissen</caption>
                <colgroup>
                  <col class="col-metric" />
                  <col class="col-dir col-oe" />
                  <col class="col-dir col-tb" />
                  <col class="col-dir col-gs" />
                  <col class="col-dir col-all" />
                  <col class="col-total" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col" class="metric-head">&nbsp;</th>
                    <th scope="col" title="Das Örtliche"><img class="logo" src="@/assets/imgs/logos/campaigns/Das-Oertliche-App-Icon.svg" alt="ÖTB" /></th>
                    <th scope="col" title="Das Telefonbuch"><img class="logo" src="@/assets/imgs/logos/campaigns/Das-Telefonbuch-App-Icon.svg" alt="TB" /></th>
                    <th scope="col" title="Gelbe Seiten"><img class="logo" src="@/assets/imgs/logos/campaigns/Gelbe-Seiten-App-Icon.svg" alt="GS" /></th>
                    <th scope="col" title="OMP"><div class="omp-label">OMP</div></th>
                    <th scope="col" class="total-head">Gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="row-presence">
                    <th scope="row" class="row-label">
                      <span class="icon-chip" aria-hidden="true"><img src="@/assets/imgs/icons/map-location-dot-solid-full.svg" alt="" /></span>
                      <span class="sr-only">Präsenz</span>
                    </th>
                    <td data-metric="presence">
                      <div :class="['indicator-wrapper presence', {'status-active': businessPartnersStore.businessPartner.postalCodeBook.oe === true, 'status-inactive': businessPartnersStore.businessPartner.postalCodeBook.oe === false, 'status-unknown': businessPartnersStore.businessPartner.postalCodeBook.oe === null}]"><a></a></div>
                    </td>
                    <td data-metric="presence">
                      <div :class="['indicator-wrapper presence', {'status-active': businessPartnersStore.businessPartner.postalCodeBook.tb === true, 'status-inactive': businessPartnersStore.businessPartner.postalCodeBook.tb === false, 'status-unknown': businessPartnersStore.businessPartner.postalCodeBook.tb === null}]"><a></a></div>
                    </td>
                    <td data-metric="presence">
                      <div :class="['indicator-wrapper presence', {'status-active': businessPartnersStore.businessPartner.postalCodeBook.gs === true, 'status-inactive': businessPartnersStore.businessPartner.postalCodeBook.gs === false, 'status-unknown': businessPartnersStore.businessPartner.postalCodeBook.gs === null}]"><a></a></div>
                    </td>
                    <td data-metric="presence">
                      <div :class="['indicator-wrapper presence', {'status-active': businessPartnersStore.businessPartner.postalCodeBook.omp === true, 'status-inactive': businessPartnersStore.businessPartner.postalCodeBook.omp === false, 'status-unknown': businessPartnersStore.businessPartner.postalCodeBook.omp === null}]"><a>?</a></div>
                    </td>
                    <td data-metric="presence">
                      <span style="display:inline-block; font-size:.9rem; opacity:.5;">–</span>
                      <span style="display:inline-block; font-size:.9rem; opacity:.5;">–</span>
                    </td>
                  </tr>
                  <tr class="row-revenue">
                    <th scope="row" class="row-label" title="Umsatz" aria-label="Umsatz">
                      <span class="icon-chip" aria-hidden="true"><img src="@/assets/imgs/icons/euro-sign-solid-full.svg" alt="" /></span>
                      <span class="sr-only">Umsatz</span>
                    </th>
                    <td class="value-cell" data-metric="revenue" :data-tip="revenueTooltip('oe')">
                      {{ revenueAmount('oe')=== null ? '–' : formatRevenue(revenueAmount('oe')) + ' €' }}
                    </td>
                    <td class="value-cell" data-metric="revenue" :data-tip="revenueTooltip('tb')">
                      {{ revenueAmount('tb')=== null ? '–' : formatRevenue(revenueAmount('tb')) + ' €' }}
                    </td>
                    <td class="value-cell" data-metric="revenue" :data-tip="revenueTooltip('gs')">
                      {{ revenueAmount('gs')=== null ? '–' : formatRevenue(revenueAmount('gs')) + ' €' }}
                    </td>
                    <td class="value-cell" data-metric="revenue" :data-tip="revenueTooltip('omp')">
                      {{ revenueAmount('omp')=== null ? '–' : formatRevenue(revenueAmount('omp')) + ' €' }}
                    </td>
                    <td class="value-cell total" data-metric="revenue-total" :data-tip="'Gesamt Umsatz: ' + (computedRevenueTotal > 0 ? formatRevenue(computedRevenueTotal) + ' €' : '–')">
                      {{ computedRevenueTotal > 0 ? formatRevenue(computedRevenueTotal) + ' €' : '–' }}
                    </td>
                  </tr>
                  <tr class="row-contracts">
                    <th scope="row" class="row-label" title="KSt Reporting" aria-label="KSt Reporting">
                      <span class="icon-chip" aria-hidden="true"><img src="@/assets/imgs/icons/chart-line-solid-full.svg" alt="" /></span>
                      <span class="sr-only">KST Reporting</span>
                    </th>
                    <td data-metric="contracts">
                      <div :class="['indicator-wrapper', { 'status-active': businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'OE'), 'status-inactive': !(businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'OE')) }]">
                        <a
                          v-if="businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'OE')"
                          title="ÖTB Verträge (Reporting öffnen)"
                          :href="businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'OE').href"
                          target="_blank"
                          aria-label="ÖTB Verträge"></a>
                        <a v-else aria-label="ÖTB Verträge"></a>
                      </div>
                    </td>
                    <td data-metric="contracts">
                      <div :class="['indicator-wrapper', { 'status-active': businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'TB'), 'status-inactive': !(businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'TB')) }]">
                        <a
                          v-if="businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'TB')"
                          title="TB Verträge (Reporting öffnen)"
                          :href="businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'TB').href"
                          target="_blank"
                          aria-label="TB Verträge"></a>
                        <a v-else aria-label="TB Verträge"></a>
                      </div>
                    </td>
                    <td data-metric="contracts">
                      <div :class="['indicator-wrapper', { 'status-active': businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'GS'), 'status-inactive': !(businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'GS')) }]">
                        <a
                          v-if="businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'GS')"
                          title="GS Verträge (Reporting öffnen)"
                          :href="businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'GS').href"
                          target="_blank"
                          aria-label="GS Verträge"></a>
                        <a v-else aria-label="GS Verträge"></a>
                      </div>
                    </td>
                    <td class="summary contracts-summary" aria-label="Alle Verträge Gesamt">
                      <span style="display:inline-block; font-size:.9rem; opacity:.5;">–</span>
                      <span style="display:inline-block; font-size:.9rem; opacity:.5;">–</span>
                    </td>
                    <td class="summary contracts-summary" aria-label="Alle Verträge Gesamt">
                      <div :class="['indicator-wrapper', { 'status-active': businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'ALL'), 'status-inactive': !(businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'ALL')) }]">
                        <a
                          v-if="businessPartnersStore.businessPartner.kstLinks && businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'ALL')"
                          :href="businessPartnersStore.businessPartner.kstLinks.find(l => l.context === 'ALL').href"
                          target="_blank"
                          aria-label="Alle Verträge Gesamt"
                          :title="'Alle Verträge im Reporting öffnen'"></a>
                        <a v-else aria-label="Alle Verträge Gesamt"></a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </transition>
        </div>
        <div class="customer-details-wrapper">
          <div class="container customerWrapper">
            <h4 class="achieved left contact"> Anschrift</h4>
            <div class="contactInfoWrapper noEdit">
              <div class="input-wrapper">
                <div v-if="businessPartnersStore.businessPartner.gp.name" class="login-field">
                  <a  target="_blank" :href="managerHost + '/website-check'" @click="handleLinkClick($event, businessPartnersStore.businessPartner.name)">
                    <img src="@/assets/imgs/icons/copy-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" :value="businessPartnersStore.businessPartner.gp.name" placeholder="Kundennamen">
                  </a>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div v-else class="login-field">
                  <div class="login-field">
                    <img src="@/assets/imgs/icons/copy-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" placeholder="Kundennamen">
                  </div>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field" v-if="(businessPartnersStore.businessPartner?.addressList?.[0]?.name || '') !== ' ,  '">
                  <a target="_blank" :href="(() => { const a = businessPartnersStore.businessPartner?.addressList?.[0]; if(!a) return ''; const parts = (a.name||'').split(','); const cityParts = (parts[1]||'').trim().split(' '); return 'https://www.google.com/maps/place/' + encodeURIComponent([a.street, a.houseNumber, cityParts[1], cityParts[2]].filter(Boolean).join(' ')); })()">
                    <img src="@/assets/imgs/icons/location-dot-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" :value="businessPartnersStore.businessPartner?.addressList?.[0]?.name || ''" placeholder="Adresse">
                  </a>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div v-else class="login-field">
                  <img src="@/assets/imgs/icons/location-dot-solid.svg" alt="Lock Icon">
                  <input readonly class="login-input" placeholder="Adresse">
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field">
                  <img src="@/assets/imgs/icons/map-solid.svg" alt="Lock Icon">
                  <input readonly class="login-input" :value="businessPartnersStore.businessPartner.tour" placeholder="Tour">
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field">
                  <img src="@/assets/imgs/icons/industry-solid.svg" alt="Lock Icon">
                  <input readonly class="login-input" :value="businessPartnersStore.businessPartner.businessCategoryList.map((businessCategory)=>businessCategory.name).join(', ')" placeholder="Branche">
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field" v-if="businessPartnersStore.businessPartner.websiteList[0]?.name">
                  <a target="_blank" :href="formatWebsiteLink(businessPartnersStore.businessPartner.websiteList[0]?.name)">
                    <img src="@/assets/imgs/icons/earth-europe-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" :value="businessPartnersStore.businessPartner.websiteList[0]?.name" placeholder="Website">
                  </a>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div v-else class="login-field">
                  <div class="login-field">
                    <img src="@/assets/imgs/icons/earth-europe-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" placeholder="Website">
                  </div>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field" v-if="businessPartnersStore.businessPartner.phoneList[0]?.name">
                  <a :href="'tel:' + businessPartnersStore.businessPartner.phoneList[0]?.name">
                    <img src="@/assets/imgs/icons/phone-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" :value="businessPartnersStore.businessPartner.phoneList[0]?.name" placeholder="Telefon">
                  </a>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div v-else class="login-field">
                  <img src="@/assets/imgs/icons/phone-solid.svg" alt="Lock Icon">
                  <input readonly class="login-input" placeholder="Telefon">
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field" v-if="businessPartnersStore.businessPartner.phoneList[1]?.name">
                  <a :href="'tel:' + businessPartnersStore.businessPartner.phoneList[1]?.name">
                    <img src="@/assets/imgs/icons/mobile-screen-button-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" :value="businessPartnersStore.businessPartner.phoneList[1]?.name" placeholder="Handynummer">
                  </a>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div v-else class="login-field">
                  <img src="@/assets/imgs/icons/mobile-screen-button-solid.svg" alt="Lock Icon">
                  <input readonly class="login-input" placeholder="Handynummer">
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div class="login-field" v-if="businessPartnersStore.businessPartner.emailList[0]?.name">
                  <a :href="'mailto:' + businessPartnersStore.businessPartner.emailList[0]?.name">
                    <img src="@/assets/imgs/icons/envelope-solid.svg" alt="Lock Icon">
                    <input readonly class="login-input" :value="businessPartnersStore.businessPartner.emailList[0]?.name" placeholder="E-Mail">
                  </a>
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
                <div v-else class="login-field">
                  <img src="@/assets/imgs/icons/envelope-solid.svg" alt="Lock Icon">
                  <input readonly class="login-input" placeholder="E-Mail">
                  <button @click="copyInputValue"><img src="@/assets/imgs/icons/copy-solid.svg"></button>
                </div>
              </div>
            </div>
          </div>
          <div class="container decision-maker">
            <slot name="decision-maker">
              <DecisionMaker
                v-if="dmProps.contacts !== undefined"
                :currentContact="dmProps.currentContact"
                :contacts="dmProps.contacts"
                :currentContactIndex="dmProps.currentContactIndex"
                :editMode="dmProps.editMode"
                :onPrev="dmProps.onPrev"
                :onNext="dmProps.onNext"
                :onAdd="dmProps.onAdd"
                :onEdit="dmProps.onEdit"
                :onSave="dmProps.onSave"
                :onUndo="dmProps.onUndo"
                :copyInputValue="dmProps.copyInputValue"
              />
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* ── Kopfzeile ── */
  .bp-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: nowrap;
    gap: 0.5rem;
    min-width: 0;
  }
  .bp-customer-name {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.25;
    color: inherit;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .bp-header-actions {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    flex-shrink: 0;
  }
  .bp-action-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: rgba(247, 247, 250, 0.6);
    border: 1px solid #e2e2ea;
    box-shadow: 0 1px 3px #0000000d;
    transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
    text-decoration: none;
    overflow: hidden;
  }
  .bp-action-chip:hover {
    background: #fff;
    box-shadow: 0 3px 8px #00000018;
    transform: translateY(-1px);
  }
  .bp-action-chip img {
    width: 18px;
    height: 18px;
    object-fit: contain;
  }
  .bp-action-chip.manager {
    width: 100px;
  }
  .bp-action-chip.manager img {
    width: 90px;
    height: auto;
  }
  @media (max-width: 640px) {
    .bp-header-row {
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 0.35rem;
    }
    .bp-customer-name {
      flex: 0 0 100%;
      text-align: center;
    }
    .bp-header-actions {
      flex: 0 0 100%;
      flex-wrap: wrap;
      justify-content: center;
    }
  }

  /* ── Bonität / Sperren Akkordeon ── */
  .bp-meta-line {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.4rem;
  }
  .bp-accordion {
    display: inline-flex;
    flex-direction: column;
    background: rgba(247, 247, 250, 0.7);
    border: 1px solid #e2e2ea;
    border-radius: 0.75rem;
    overflow: hidden;
    box-shadow: 0 1px 3px #0000000d;
    min-width: 130px;
  }
  .bp-accordion-toggle {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    padding: 0.3rem 0.75rem;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 500;
    color: #444;
    text-align: left;
    width: 100%;
    transition: background 0.14s;
  }
  .bp-accordion-toggle:hover { background: rgba(0,0,0,0.04); }
  .bp-accordion-label {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #888;
  }
  .bp-accordion-count {
    font-size: 0.7rem;
    color: #aaa;
    margin-left: 0.1rem;
  }
  .bp-accordion-chevron {
    width: 0.65rem;
    height: 0.65rem;
    margin-left: auto;
    opacity: 0.45;
    transition: transform 0.2s;
    transform: rotate(180deg);
  }
  .bp-accordion-chevron.rotated {
    transform: rotate(0deg);
  }
  .bp-accordion-list {
    list-style: none;
    margin: 0;
    padding: 0.45rem 0 0.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.42rem;
  }
  .bp-accordion-list li {
    max-width: 100%;
    width: 100%;
  }
  .bp-status-section {
    display: grid;
    gap: 0.7rem;
    padding: 0.7rem 0 0.15rem;
  }
  .bp-status-section.single {
    gap: 0;
    padding-top: 0.35rem;
    padding-bottom: 0;
  }
  .bp-status-section.single .bp-accordion-list {
    padding-left: 0;
  }
  :deep(.bp-accordion-list) {
    padding-left: 0 !important;
  }
  .bp-status-single-row,
  .bp-status-entry {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.84rem;
    color: #333;
    line-height: 1.35;
    padding: 0.62rem 0.85rem;
    background: #f7f7fa;
    border: 1px solid #e2e2ea;
    border-radius: 0.75rem;
    box-shadow: 0 1px 2px #0001;
    transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
  }
  .bp-status-single-row::before,
  .bp-status-entry::before {
    content: none !important;
    display: none !important;
  }
  .bp-status-single-row:hover,
  .bp-status-entry:hover {
    background: #ffffff;
    box-shadow: 0 3px 10px #00000010;
    transform: translateY(-1px);
  }
  .bp-status-title {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #888;
    white-space: nowrap;
  }
  .bp-title-icon {
    width: 0.85rem;
    height: 0.85rem;
    object-fit: contain;
    opacity: 0.8;
    margin-right: 0.28rem;
    vertical-align: -1px;
  }
  .sperren-heading,
  .sperren-title {
    display: inline-flex;
    align-items: center;
  }
  .sperren-heading::before,
  .sperren-title::before {
    content: none !important;
    display: none !important;
  }
  .bp-status-chevron {
    width: 0.75rem;
    height: 0.75rem;
    opacity: 0.5;
    margin-left: auto;
    margin-right: 0.1rem;
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: rotate(180deg);
  }
  .bp-status-chevron.rotated {
    transform: rotate(0deg);
  }
  .bp-status-dot {
    flex-shrink: 0;
    width: 0.68rem;
    height: 0.68rem;
    border-radius: 50%;
    background: #ccc;
    border: 1.5px solid #b0b0b0;
  }
  /* Toggle dot inherits color from its parent button class */
  .bp-accordion-toggle.color-green .bp-status-dot {
    background: #b7e7ce;
    border-color: #2fac6a;
    box-shadow: 0 0 0 2px #2fac6a18;
  }
  .bp-accordion-toggle.color-red .bp-status-dot {
    background: #f5b7b7;
    border-color: #c71418;
    box-shadow: 0 0 0 2px #c7141818;
  }
  .bp-accordion-toggle.color-orange .bp-status-dot {
    background: #fde7b7;
    border-color: #e08a00;
    box-shadow: 0 0 0 2px #e08a0018;
  }
  .bp-accordion-toggle.color-neutral .bp-status-dot {
    background: #e2e2ea;
    border-color: #bbb;
  }
  /* List entry & single-row dots */
  .bp-status-entry.color-green .bp-status-dot,
  .bp-status-single-row.color-green .bp-status-dot {
    background: #b7e7ce;
    border-color: #2fac6a;
    box-shadow: 0 0 0 2px #2fac6a18;
  }
  .bp-status-entry.color-green .bp-status-text,
  .bp-status-single-row.color-green .bp-status-text { color: #1a7a49; }
  .bp-status-entry.color-red .bp-status-dot,
  .bp-status-single-row.color-red .bp-status-dot {
    background: #f5b7b7;
    border-color: #c71418;
    box-shadow: 0 0 0 2px #c7141818;
  }
  .bp-status-entry.color-red .bp-status-text,
  .bp-status-single-row.color-red .bp-status-text { color: #a01015; }
  .bp-status-entry.color-orange .bp-status-dot,
  .bp-status-single-row.color-orange .bp-status-dot {
    background: #fde7b7;
    border-color: #e08a00;
    box-shadow: 0 0 0 2px #e08a0018;
  }
  .bp-status-entry.color-orange .bp-status-text,
  .bp-status-single-row.color-orange .bp-status-text { color: #8a5200; }
  .bp-status-entry.color-neutral .bp-status-dot,
  .bp-status-single-row.color-neutral .bp-status-dot {
    background: #e2e2ea;
    border-color: #bbb;
  }
  .bp-status-text {
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  .legend-names-toggle-row {
    display: flex;
    align-items: center;
    gap: 1.1em;
    margin-bottom: 0.7em;
  }
  .status-legend {
    display: flex;
    align-items: center;
    gap: 0.5em;
    font-size: 0.97em;
    color: #666;
    user-select: none;
  }
  .legend-dot {
    display: inline-block;
    width: 0.72em;
    height: 0.72em;
    border-radius: 50%;
    margin-right: 0.18em;
    vertical-align: middle;
    border: 1.2px solid #e2e2ea;
    box-shadow: 0 1px 4px #0001;
    background: linear-gradient(180deg, #fff 60%, #f3f3f7 100%);
    position: relative;
    transition: box-shadow 0.18s, border 0.18s;
  }
  .legend-dot-aktiv {
    background: #b7e7ce;
    border-color: #2FAC6A;
    box-shadow: 0 1px 4px #2fac6a22;
  }
  .legend-dot-inaktiv {
    background: #e7b7b7;
    border-color: #C71418;
    box-shadow: 0 1px 4px #c7141822;
  }
  .legend-label {
    font-size: 0.97em;
    color: #666;
    margin-right: 0.7em;
    margin-left: 0.1em;
  }
  .modern-toggle {
    /* background: #f7f7fa;
    border: 1px solid #e2e2ea;
    color: #444; */
    /* padding: 0.22em 1.1em;
    border-radius: 0.7em; */
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;
    cursor: pointer;
    margin-left: auto;
    /* font-weight: 500;
    letter-spacing: 0.01em;
    box-shadow: 0 1px 4px #0001;
    outline: none; */
    display: inline-block;
    padding: 0.22em 1.1em;
  }
  .modern-toggle span {
    font-size: 0.75em;
    /* padding: 0.22em 1.1em; */
  }

  .avatars-pill {
  display: flex;
  align-items: center;
  background-color: rgba(247, 247, 250, .5);
  border-radius: 999px;
  padding: 0 1rem;
  box-shadow: 0 1px 4px #0001;
  min-width: 2.2em;
  margin-right: 0.2em;
  gap: 0.18em;
}
.avatars-pill .sm-wrapper {
 padding: .5em 0
}
.avatars-pill .avatar-wrapper {
  margin-right: 0.1em;
}
.avatars-pill .avatar-wrapper:last-child {
  margin-right: 0;
}
</style>
